// ===================== React =====================
import { useState } from "react";

// ===================== MUI =====================
import {
  Modal,
  Box,
  Typography,
  Divider,
  Grid,
  Paper,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";

// ===================== Icons =====================
import DownloadIcon from "@mui/icons-material/Download";

// ===================== Assets =====================
import sperologo from "../../../../assets/sperologo.png";

// ===================== PDF =====================
import { pdf } from "@react-pdf/renderer";
import LongTermReportPDF from "./LongTermReportPDF";
import { API_URL } from "../../../Config/api";

const styles = `
  .section-avoid-break {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  .page-break {
    page-break-before: always;
    break-before: page;
  }
`;

const SectionCard = ({ title, children, className }) => (
  <Paper
    elevation={0} // ❌ shadow remove (print rule)
    className={className}
    style={{
      padding: "18px 20px", // 🔥 more air
      borderRadius: 8,
      marginBottom: 22, // 🔥 section separation
      background: "#ffffff",
      border: "1px solid #cfcfcf", // lighter border
    }}
  >
    <Box sx={{ borderLeft: "6px solid #16c7c7", pl: 1.5, mb: 1 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{title}</Typography>
    </Box>

    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

const GridField = ({ label, value, md }) => (
  <Grid item xs={12} sm={6} md={md ?? 4}>
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: 14.5,
        color: "#222",
        mb: 0.3,
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        fontSize: 13,
        color: "#777",
        lineHeight: 1.5,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        minHeight: 18,
      }}
    >
      {value || "—"}
    </Typography>
  </Grid>
);

const ReportFooter = () => (
  <Box
    className="pdf-footer"
    sx={{
      borderTop: "1px solid #ccc",
      mt: 3,
      pt: 1.2,
      fontSize: 11,
      color: "#666",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography fontSize={11}>
      © {new Date().getFullYear()} Spero Healthcare
    </Typography>

    <Typography fontSize={11}>
      Generated on: {new Date().toLocaleString()}
    </Typography>
  </Box>
);

// const downloadPDF = () => {
//   const content = document.getElementById("pdf-content");

//   // A4 size in pixels at 96 DPI: 595 x 842
//   const a4Width = 595;
//   const a4Height = 842;

//   html2canvas(content, {
//     scale: 3,           // higher scale = better quality
//     useCORS: true
//   }).then((canvas) => {
//     const imgData = canvas.toDataURL("image/jpeg", 1.0);

//     const pdf = new jsPDF({
//       unit: "px",
//       format: "a4",
//       orientation: "portrait"
//     });

//     // Calculate ratio to fit the content into A4
//     const ratio = Math.min(a4Width / canvas.width, a4Height / canvas.height);
//     const imgWidth = canvas.width * ratio;
//     const imgHeight = canvas.height * ratio;

//     const xOffset = (a4Width - imgWidth) / 2;
//     const yOffset = (a4Height - imgHeight) / 2;

//     pdf.addImage(imgData, "JPEG", xOffset, yOffset, imgWidth, imgHeight);
//     pdf.save("LongTermReport.pdf");
//   });
// };

const LongTermReportModal = ({ open, onClose, reportData }) => {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [progress, setProgress] = useState(0);

  const startFakeProgress = () => {
    setProgress(0);

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return prev;
        }
        return prev + Math.floor(Math.random() * 6) + 2; // 2–8%
      });
    }, 300);

    return interval;
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDateOnly = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const formatDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  const getFormattedAge = (data) => {
  const years = data?.Age;
  const months = data?.Age_month;
  const days = data?.Age_day;

  const parts = [];

  if (years !== null && years !== undefined) parts.push(`${years} Y`);
  if (months !== null && months !== undefined) parts.push(`${months} M`);
  if (days !== null && days !== undefined) parts.push(`${days} D`);

  return parts.length ? parts.join(" ") : "-";
};

  //   setIsDownloading(true);

  //   const element = document.getElementById("pdf-content");

  //   const opt = {
  //    margin: [20, 18, 20, 18],
  //     filename: `Long_Term_Report_${reportData?.name || "Patient"}.pdf`,
  //     image: { type: "jpeg", quality: 0.98 },

  //     html2canvas: {
  //       scale: 2,
  //       useCORS: true,
  //       scrollY: 0,
  //     },

  //     jsPDF: {
  //       unit: "mm",
  //       format: "a4",
  //       orientation: "portrait",
  //     },

  //     pagebreak: {
  //       mode: ["css"],
  //       avoid: [".section-avoid-break"],
  //     },
  //   };

  //   try {
  //     await html2pdf().set(opt).from(element).save();
  //     showSnackbar("PDF downloaded successfully", "success");
  //   } catch (e) {
  //     showSnackbar("Failed to generate PDF", "error");
  //   } finally {
  //     setIsDownloading(false);
  //   }
  // };

  // const downloadPDF = async () => {
  //   const content = document.getElementById("pdf-content");

  //   const canvas = await html2canvas(content, {
  //     scale: 2,
  //     useCORS: true,
  //     scrollY: -window.scrollY,
  //   });

  //   const imgData = canvas.toDataURL("image/png");
  //   const pdf = new jsPDF("p", "mm", "a4");

  //   const pdfWidth = pdf.internal.pageSize.getWidth();
  //   const pdfHeight = pdf.internal.pageSize.getHeight();

  //   const imgWidth = pdfWidth;
  //   const imgHeight = (canvas.height * imgWidth) / canvas.width;

  //   let yOffset = 0;

  //   while (yOffset < imgHeight) {
  //     pdf.addImage(imgData, "PNG", 0, -yOffset, imgWidth, imgHeight);

  //     yOffset += pdfHeight;

  //     if (yOffset < imgHeight) {
  //       pdf.addPage();
  //     }
  //   }

  //   pdf.save("LongTermReport.pdf");
  // };

  const handleGeneratePDF = async () => {
    try {
      setPdfLoading(true);

      const progressInterval = startFakeProgress();

      await new Promise(requestAnimationFrame);

      const blob = await pdf(
        <LongTermReportPDF reportData={reportData} />
      ).toBlob();

      clearInterval(progressInterval);
      setProgress(100);

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Long_Term_Report_${reportData?.name}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showSnackbar("Failed to generate PDF", "error");
    } finally {
      setTimeout(() => {
        setPdfLoading(false);
        setProgress(0);
      }, 600);
    }
  };

  const assessmentBodyRows = [
    {
      label: "HEENT",
      flag: reportData?.heent,
      remark: reportData?.heent_remark,
    },
    { label: "Neck", flag: reportData?.neck, remark: reportData?.neck_remark },
    {
      label: "Chest",
      flag: reportData?.chest,
      remark: reportData?.chest_remark,
    },
    {
      label: "Abdomen",
      flag: reportData?.abdomen,
      remark: reportData?.abdomen_remark,
    },
    {
      label: "Pelvis",
      flag: reportData?.pelvis,
      remark: reportData?.pelvis_remark,
    },
    { label: "Back", flag: reportData?.back, remark: reportData?.back_remark },
    {
      label: "Upper Limb",
      flag: reportData?.upper_limb,
      remark: reportData?.upper_limb_remark,
    },
    {
      label: "Lower Limb",
      flag: reportData?.lower_limb,
      remark: reportData?.lower_limb_remark,
    },
    {
      label: "General",
      flag: reportData?.general,
      remark: reportData?.general_remark,
    },
  ];

  return (
    <>
      <style>{styles}</style>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            width: 850,
            bgcolor: "#fff",
            p: 3,
            mt: 3,
            mx: "auto",
            borderRadius: 2,
            maxHeight: "88vh",
            overflowY: "auto",
          }}
        >
          <div
            id="pdf-content"
            style={{
              width: "780px", // 🔥 794 se thoda kam = better margins
              margin: "0 auto",
              // subtle inner padding
              background: "#fff",
              padding: "0 8px 60px",
            }}
          >
            {/* ================= HEADER ================= */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: 85,
                mb: 3,
                borderBottom: "1px solid #ddd",
                overflow: "hidden", // ✅ VERY IMPORTANT
              }}
            >
              {/* Left triangle */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: 0,
                  height: 0,
                  borderTop: "90px solid #16c7c7",
                  borderRight: "90px solid transparent",
                }}
              />

              {/* Skewed center */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "5%",
                  width: "90%", // full width relative to modal
                  height: "100%",
                  backgroundColor: "#f2f2f2",
                  transform: "skewX(-20deg)",
                }}
              />

              {/* Right small rectangle */}
              <Box
                sx={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  width: "15%", // relative width
                  height: 14,
                  backgroundColor: "#f5a623",
                }}
              />

              {/* Text & Logo */}
              <Box
                sx={{
                  position: "relative",
                  zIndex: 2,
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2.5,
                }}
              >
                <Box
                  sx={{
                    border: "2px solid #000",
                    px: 2,
                    py: 0.5,
                    fontWeight: 800,
                    fontSize: 18,
                    bgcolor: "#ffffff",
                  }}
                >
                  LONG TERM REPORT
                </Box>
                <img src={sperologo} alt="logo" height={55} />
              </Box>
            </Box>

            {/* ================= LEFT + RIGHT COLUMNS ================= */}
            <Box
              sx={{
                p: 3,
                backgroundColor: "#fff",
              }}
            >
              <Grid container spacing={3}>
                {/* LEFT COLUMN */}
                <Grid item xs={12} md={6}>
                  {/* PERSONAL INFO */}
                  <SectionCard
                    title="Personal Information"
                    className="section-avoid-break"
                  >
                    <Grid container spacing={2}>
                      <GridField label="Name" value={reportData?.name} />
                      <GridField
                        label="Patient Number"
                        value={reportData?.phone_no}
                      />
                      <GridField
                        label="Date of Birth"
                        value={reportData?.dob}
                      />
                   <GridField
  label="Age"
  value={getFormattedAge(reportData)}
/>
                      <GridField label="Gender" value={reportData?.gender_id} />
                      <GridField
                        label="Marital Status"
                        value={reportData?.marital_status}
                      />
                      <GridField
                        label="Blood Group"
                        value={reportData?.blood_group}
                      />
                      <GridField label="Height" value={reportData?.height} />
                      <GridField label="Weight" value={reportData?.weight} />
                      <GridField
                        label="Identification Mark"
                        value={reportData?.identification_mark}
                      />
                      <GridField label="State" value={reportData?.state_id} />
                      <GridField label="City" value={reportData?.city_id} />
                      <GridField label="Address" value={reportData?.address} />
                      <GridField label="Pincode" value={reportData?.pincode} />
                      <GridField
                        label="Allergies"
                        value={reportData?.allergies}
                      />
                      <GridField
                        label="Vaccinations"
                        value={reportData?.vaccinations}
                      />
                      <GridField
                        label="Insurance Provider"
                        value={reportData?.insurance}
                      />
                      <GridField
                        label="Family Doctor Name"
                        value={reportData?.family_doctor}
                      />
                      <GridField
                        label="Family Doctor Phone"
                        value={reportData?.family_doctor_phone}
                      />
                      <GridField
                        label="Family Doctor Email"
                        value={reportData?.family_doctor_email}
                      />
                      <GridField
                        label="Spouse Name"
                        value={reportData?.spouse_name}
                      />
                      <GridField
                        label="Spouse Phone"
                        value={reportData?.spouse_phone}
                      />
                      <GridField
                        label="Spouse Email"
                        value={reportData?.spouse_email}
                      />
                      <GridField
                        label="Past Medical History"
                        value={reportData?.past_medical_history}
                      />
                      <GridField
                        label="Tobacco Remark"
                        value={reportData?.tobacco_remark}
                      />
                      <GridField
                        label="Alcohol Remark"
                        value={reportData?.alcohol_remark}
                      />
                    </Grid>
                  </SectionCard>

                  <SectionCard
                    title="Current Treatment Plan"
                    className="section-avoid-break"
                  >
                    <Grid container spacing={2}>
                      <GridField
                        label="Tablet"
                        value={reportData?.ctp_tablet}
                        md={12}
                      />
                      <GridField
                        label="Injection"
                        value={reportData?.ctp_injection}
                        md={12}
                      />
                      <GridField
                        label="IV Fluids"
                        value={reportData?.ctp_iv_fluids}
                        md={12}
                      />
                      <GridField
                        label="Interventions"
                        value={reportData?.ctp_interventions}
                        md={12}
                      />
                      <GridField
                        label="Notes"
                        value={reportData?.ctp_notes}
                        md={12}
                      />
                    </Grid>
                  </SectionCard>

                  <SectionCard
                    title="Assessment Body"
                    className="section-avoid-break"
                  >
                    <Grid container spacing={1}>
                      {/* Section Title */}
                      <Grid item xs={12}>
                        <Typography fontWeight={700} fontSize={14}>
                          Assessment Body Remark
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                      </Grid>

                      {/* Column Headers */}
                      <Grid item xs={12}>
                        <Grid container>
                          <Grid item xs={3}>
                            <Typography fontWeight={600}>Body Part</Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography fontWeight={600}>Yes / No</Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography fontWeight={600}>Remark</Typography>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Rows */}
                      {assessmentBodyRows.map((row) => (
                        <Grid item xs={12} key={row.label}>
                          <Grid
                            container
                            sx={{
                              border: "1px solid #000",
                              p: 1,
                              borderRadius: 1,
                            }}
                          >
                            <Grid item xs={3}>
                              <Typography fontSize={13}>{row.label}</Typography>
                            </Grid>

                            <Grid item xs={2}>
                              <Typography fontSize={13}>
                                {row.flag ? "Yes" : "No"}
                              </Typography>
                            </Grid>

                            <Grid item xs={7}>
                              <Typography
                                fontSize={13}
                                sx={{
                                  wordBreak: "break-word",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {row.remark || "—"}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      ))}

                      {/* ================= BODY IMAGE BELOW ================= */}
                      <Grid item xs={12} mt={2}>
                        <Typography fontWeight={700} fontSize={14}>
                          Assessment Body Image
                        </Typography>
                        <Divider sx={{ mb: 1 }} />

                        {reportData?.patient_image ? (
                          <Box className="section-avoid-break">
                            <Box
                              component="img"
                              src={`${API_URL}${reportData.patient_image}`}
                              sx={{
                                width: "100%",
                                maxHeight: 280,
                                objectFit: "contain",
                                border: "1px solid #000",
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography fontSize={13} color="text.secondary">
                            No image available
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </SectionCard>
                </Grid>

                {/* RIGHT COLUMN */}
                <Grid item xs={12} md={6}>
                  <SectionCard
                    title="Diagnosis / Medical"
                    className="section-avoid-break"
                  >
                    <Grid container spacing={2}>
                      <GridField
                        label="Diagnosis"
                        value={reportData?.diagnosis}
                      />
                      <GridField label="Hospital" value={reportData?.hosp_id} />
                      <GridField
                        label="Primary Consultant"
                        value={reportData?.primary_consultant_name}
                      />
                      <GridField
                        label=" Primary Consultant Phone"
                        value={reportData?.primary_consultant_phone}
                      />
                      <GridField
                        label=" Primary Consultant Email"
                        value={reportData?.primary_consultant_email}
                      />
                      <GridField
                        label="Other Consultant"
                        value={reportData?.other_consultant_name}
                      />
                      <GridField
                        label=" Other Consultant Phone"
                        value={reportData?.other_consultant_phone}
                      />
                      <GridField
                        label=" Other Consultant Email"
                        value={reportData?.other_consultant_email}
                      />
                      <GridField
                        label="Duration From - To"
                        value={`${formatDateOnly(
                          reportData?.hospitalization_from
                        )} to ${formatDateOnly(
                          reportData?.hospitalization_to
                        )}`}
                      />
                    </Grid>
                  </SectionCard>

                  <SectionCard
                    title="Follow-up Instructions"
                    className="section-avoid-break"
                  >
                    <Grid container columnSpacing={2.5} rowSpacing={2}>
                      {/* 🔹 LONG TEXT → FULL WIDTH */}
                      <GridField
                        label="Physical"
                        value={reportData?.fi_physical}
                        md={12}
                      />

                      <GridField
                        label="Investigations"
                        value={reportData?.fi_investigations}
                        md={12}
                      />

                      <GridField
                        label="Interventions"
                        value={reportData?.fi_interventions}
                        md={12}
                      />

                      <GridField
                        label="Assistance"
                        value={reportData?.fi_assistance}
                        md={12}
                      />

                      {/* 🔹 SHORT INFO → 2 COLUMNS */}
                      <GridField
                        label="Referred By"
                        value={reportData?.reffered_by}
                        md={6}
                      />

                      <GridField
                        label="Referred Phone Number"
                        value={reportData?.reffered_by_phone}
                        md={6}
                      />

                      <GridField
                        label="Referred Email"
                        value={reportData?.reffered_by_email}
                        md={6}
                      />
                    </Grid>
                  </SectionCard>

                  {/* left sections */}

                  {/* ASSESSMENT BY PHYSICIAN */}
                  <SectionCard
                    title="Assessment by Physician"
                    className="section-avoid-break"
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography sx={{ fontWeight: 700 }}>
                          Assessment place
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "#777",
                            lineHeight: 1.4,

                            /* 🔑 THIS IS THE FIX */
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",

                            /* visual stability */
                            minHeight: 18,
                          }}
                        >
                          {reportData?.assessment_place || "—"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography sx={{ fontWeight: 700 }}>
                          {" "}
                          Physician Name
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "#777",
                            lineHeight: 1.4,

                            /* 🔑 THIS IS THE FIX */
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",

                            /* visual stability */
                            minHeight: 18,
                          }}
                        >
                          {reportData?.doctor_name || "—"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography sx={{ fontWeight: 700 }}>
                          Baseline
                        </Typography>
                        <Divider />
                      </Grid>

                      <GridField label="AVPU" value={reportData?.baseline} />
                      <GridField label="Airway" value={reportData?.airway} />
                      <GridField
                        label="Breathing"
                        value={reportData?.breathing}
                      />
                      <GridField
                        label="Circulation"
                        value={reportData?.circulation}
                      />
                      <GridField
                        label="Skin Perfusion"
                        value={reportData?.skin_perfusion}
                      />
                      <GridField label="Temp (°F)" value={reportData?.temp} />
                      <GridField
                        label="Pulse (/min)"
                        value={reportData?.pulse}
                      />
                      <GridField label="RR (/min)" value={reportData?.rr} />
                      <GridField label="BP (mmHg)" value={reportData?.bp} />
                      <GridField label="BSL (mg/dl)" value={reportData?.bsl} />
                      <GridField label="SpO₂ (%)" value={reportData?.spo2} />
                      <GridField
                        label="GCS (Out of 15)"
                        value={reportData?.gst_total}
                      />

                      <Grid item xs={12}>
                        <Typography sx={{ fontWeight: 700 }}>
                          General Body Notes
                        </Typography>
                        <Divider />
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "#777",
                            lineHeight: 1.4,

                            /* 🔑 THIS IS THE FIX */
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",

                            /* visual stability */
                            minHeight: 18,
                          }}
                        >
                          {reportData?.General_Body_Notes || "—"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography sx={{ fontWeight: 700 }}>
                          Clinical Impression
                        </Typography>
                        <Divider />
                        <Typography>
                          {reportData?.clinical_impression || "—"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </SectionCard>
                  
                   <SectionCard
                    title="Recent Investigations"
                    className="section-avoid-break"
                  >
                    <Grid container spacing={2}>
                      <GridField
                        label="Cbc"
                        value={reportData?.recent_invesigation_cbc}
                        md={12}
                      />  
                      <GridField
                        label="rft"
                        value={reportData?.rft}
                        md={12}
                      />  
                      {/* <GridField
                        label="bsl"
                        value={reportData?.bsl_remark}
                        md={12}
                      />   */}
                      <GridField
                        label="electrocycles"
                        value={reportData?.electrocycles}       
                          
                        md={12}
                      />
                      <GridField
                        label="other"
                        value={reportData?.other}       
                          
                        md={12}
                      />
                    </Grid>
                 
                
                  </SectionCard>

                  <SectionCard
                    title="HHC Service Reference"
                    className="section-avoid-break"
                  >
                    {/* ===== Select Services ===== */}
                    <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1 }}>
                      Select Services
                    </Typography>

                    <Divider sx={{ mb: 1 }} />

                    <Grid container spacing={1}>
                      {Array.isArray(reportData?.services) &&
                      reportData.services.length > 0 ? (
                        reportData.services.map((item, index) => (
                          <Grid item xs={12} key={index}>
                            {/* Service */}
                            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                              • {item.service_name}
                            </Typography>

                            {/* Sub-services */}
                            {Array.isArray(item.sub_services) &&
                              item.sub_services.length > 0 &&
                              item.sub_services.map((sub, i) => (
                                <Typography
                                  key={i}
                                  sx={{ fontSize: 13, ml: 2 }}
                                >
                                  - {sub}
                                </Typography>
                              ))}
                          </Grid>
                        ))
                      ) : (
                        <Typography sx={{ fontSize: 13, color: "#777", ml: 1 }}>
                          — No Services Selected —
                        </Typography>
                      )}
                    </Grid>

                    {/* ===== Patient / Guardian Details ===== */}
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                        Patient / Guardian Details
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                    </Box>

                    <Grid container spacing={2}>
                      <GridField
                        label="Patient Name"
                        value={reportData?.name || "—"}
                      />

                      <GridField
                        label="Form Fill-up Date & Time"
                        value={formatDateTime(reportData?.form_fillup_date)}
                      />

                      <GridField
                        label="Physician Name"
                        value={reportData?.notify_name || "—"}
                      />
                    </Grid>

                    {/* ===== Signatures Area ===== */}
                 <Grid container spacing={2} sx={{ mt: 1 }}>
  {/* Patient / Guardian Signature */}
  <Grid item xs={12} md={6}>
    <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>
      Patient Signature
    </Typography>

    <Box
      sx={{
        height: 90,
        border: "1px dashed #777",
        borderRadius: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#fafafa",
      }}
    >
      {reportData?.patient_signature ? (
        <Box
          component="img"
          src={`${API_URL}${reportData.patient_signature}`}
          alt="Patient Signature"
          sx={{
            maxHeight: "100%",
            maxWidth: "100%",
            objectFit: "contain",
          }}
        />
      ) : (
        <Typography sx={{ fontSize: 12, color: "#999" }}>
          No Signature
        </Typography>
      )}
    </Box>
  </Grid>

  {/* Physician Signature */}
  <Grid item xs={12} md={6}>
    <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>
      Physician Signature
    </Typography>

    <Box
      sx={{
        height: 90,
        border: "1px dashed #777",
        borderRadius: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#fafafa",
      }}
    >
      {reportData?.physician_signature ? (
        <Box
          component="img"
          src={`${API_URL}${reportData.physician_signature}`}
          alt="Physician Signature"
          sx={{
            maxHeight: "100%",
            maxWidth: "100%",
            objectFit: "contain",
          }}
        />
      ) : (
        <Typography sx={{ fontSize: 12, color: "#999" }}>
          No Signature
        </Typography>
      )}
    </Box>
  </Grid>
</Grid>
                  </SectionCard>
                </Grid>
              </Grid>
            </Box>
            <ReportFooter />
          </div>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 3,
              borderTop: "1px solid #eee",
              mt: 2,
            }}
          >
            <Button
              variant="contained"
              startIcon={
                pdfLoading ? <CircularProgress size={16} /> : <DownloadIcon />
              }
              onClick={handleGeneratePDF}
              disabled={pdfLoading}
            >
              {pdfLoading ? `Generating PDF… ${progress}%` : "Download PDF"}
            </Button>
          </Box>
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Modal>
    </>
  );
};

export default LongTermReportModal;
